import { AccommodationContactAttributes } from '@/types'
import { UpdateAccommodationContactMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { contactTypes } from './contact_types'
import { Badge } from '../event_contact_type/badge'
import { useUserContact } from './useUserContact'
import { EditAccommodationContactQuery } from './__generated__/edit_query'
import { Inline as Loading } from '@/components/loading'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Button } from '../base/button'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as EditQuery } from './edit_query'
import { Inline as Error } from '@/components/error'

type Accommodation = EditAccommodationContactQuery['accommodations']['nodes'][0]

interface Props {
  accommodationId: string
  id: string
  onSuccess: () => void
}

export const Form = ({ accommodationId, id, onSuccess }: Props) => {
  const {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading
  } = useForm<UpdateAccommodationContactMutation,
    AccommodationContactAttributes,
    EditAccommodationContactQuery,
    Accommodation>({
    queryVariables: { accommodationId, accommodationContactId: id },
    id,
    initializer: accommodation => {
      const accommodationContact = accommodation.accommodationContacts.nodes[0]
      return {
        accommodationId,
        userId: accommodationContact.user?.id || '',
        name: accommodationContact.name,
        email: accommodationContact.email,
        phone: accommodationContact.phone,
        contactType: accommodationContact.contactType,
      }
    },
    query: EditQuery,
    queryKey: 'accommodations',
    mutation: UpdateMutation,
    resultKey: 'updateAccommodationContact',
    refetchQueries: [TableQuery]
  })

  const [showDelete, setShowDelete] = useState(false)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  const { pickedUser } = useUserContact({ attributes, setAttributes })

  const onDelete = () => {
    setShowDelete(false)
    onSuccess()
  }

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { formBuilder.buildField({ name: 'Person', field: 'userId', type: 'user' }) }
        { formBuilder.buildField({ name: 'Name', field: 'name', type: 'text', disabled: pickedUser }) }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Email', field: 'email', type: 'email', disabled: pickedUser }) }
          { formBuilder.buildField({ name: 'Phone', field: 'phone', type: 'text', disabled: pickedUser }) }
        </div>
        { formBuilder.buildField({
          name: 'Contact type',
          field: 'contactType',
          type: 'select',
          options: Object.keys(contactTypes).map(ct => ({
            value: ct,
            display: <Badge eventContact={contactTypes[ct]} />
          }))
        })}
        <div className="flex flex-row justify-between">
          <Button color="red" onClick={() => setShowDelete(true) }>Delete</Button>
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
        </div>
        <DeleteDialog
          id={id}
          mutation={DeleteMutation}
          open={showDelete}
          refetchQueries={[TableQuery]}
          setOpen={setShowDelete}
          onSuccess={onDelete}
        />
      </div>
    </>
  )
}
